/**
 * BrandingAdmin.tsx — Admin > Branding.
 *
 * The corporate dashboard's branding editor for the signed-in account, as a
 * settings page (corp lists every account and edits one in a dialog; here there
 * is one account, so the form is the page):
 *   - Theme colours: Background Color 1 / 2, Button Color, Tab Color (a colour
 *     picker and a hex field each);
 *   - Logos, two sets a row, each a Square and a Full slot: Site Logo, SVG
 *     Logo (SVG files only), Light Logo, Dark Logo. A slot shows its logo on
 *     the background it is meant for, with Preview and Change on hover; an
 *     empty slot shows the legacy file the navigation falls back to, marked
 *     "legacy fallback", or Upload. Uploads go to the Logos photo folder.
 *   - Under the Site Logo, the navigation preview and the caption (Sub Text,
 *     Font, Size, Spacing, Color, Justification, Move Y).
 * Save sits top right, as on the other settings pages, with Unsaved changes
 * and Revert beside it; changed fields carry the "modified" pill. Saving
 * refreshes the navigation so the new logo and colours show straight away.
 * (Corp's "Migrate to S3" stays in the corporate dashboard.)
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the form sits in an
 * AdminLockedFieldset, so every field shows its value read-only; there is no
 * Save, Revert, logo Upload or Change, colour picker (a swatch shows the
 * colour), caption colour reset or Justification change. Preview still opens.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { AlertTriangle, ImageIcon, RotateCcw, Upload, ZoomIn } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DESCRIPTION_CLASS, LABEL_CLASS } from '@/components/admin/DialogChrome';
import { EntityImagePicker } from '@/components/admin/EntityImagePicker';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  COLOR_FIELDS,
  LOGO_SETS,
  SITE_LOGO_SUB_TEXT_MAX,
  brandingFormFrom,
  brandingPayload,
  getAccountBranding,
  legacyFallbackUrl,
  resolveLogoUrl,
  updateAccountBranding,
  type BrandingForm,
  type LogoKey,
} from '@/services/branding.service';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';
import {
  LOGO_SUB_TEXT_ALIGNMENTS,
  LOGO_SUB_TEXT_DEFAULT_LETTER_SPACING_PX,
  LOGO_SUB_TEXT_DEFAULT_SIZE_PX,
  LOGO_SUB_TEXT_FONT_OPTIONS,
  LOGO_SUB_TEXT_NAV_LOGO_HEIGHT_PX,
  logoSubTextColor,
  logoSubTextFontSize,
  logoSubTextFontStack,
  logoSubTextLetterSpacing,
  logoSubTextOffsetY,
} from '@/v5/lib/logoSubTextFonts';

const CHECKER: CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #d1d5db 25%, transparent 25%), linear-gradient(-45deg, #d1d5db 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d1d5db 75%), linear-gradient(-45deg, transparent 75%, #d1d5db 75%)',
  backgroundSize: '12px 12px',
  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
  backgroundColor: '#f9fafb',
};

const FIELD_LABELS: Partial<Record<keyof BrandingForm, string>> = {
  siteLogoSubText: 'Sub Text',
  siteLogoSubTextFont: 'Font',
  siteLogoSubTextSize: 'Size',
  siteLogoSubTextLetterSpacing: 'Spacing',
  siteLogoSubTextColor: 'Color',
  siteLogoSubTextAlign: 'Justification',
  siteLogoSubTextOffsetY: 'Move Y',
};

/** "#rrggbb" for the colour input, from a stored hex. */
function toHexInput(v: string): string {
  return v ? `#${v.replace(/^#/, '').padEnd(6, '0').slice(0, 6)}` : '#000000';
}

export function BrandingAdmin() {
  const accountId = useAuthStore((s) => s.accountId);
  const queryClient = useQueryClient();
  const locked = useAdminPageLevel() === 'locked';
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState<{ key: LogoKey; svg: boolean } | null>(null);
  const [preview, setPreview] = useState<{ url: string; bg: 'white' | 'dark' } | null>(null);
  const t = useTrackedForm<BrandingForm>(() => brandingFormFrom(null));
  const { reset } = t;

  useEffect(() => {
    let cancelled = false;
    getAccountBranding()
      .then((row) => {
        if (!cancelled) reset(brandingFormFrom(row));
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(true);
        toast.error(getErrorMessage(err, 'Failed to load branding'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reset]);

  const label = useCallback((key: keyof BrandingForm) => FIELD_LABELS[key] ?? String(key), []);
  const badge = (key: keyof BrandingForm, text?: string) => t.badge(key, text ?? label(key));

  async function save() {
    if (locked) return;
    setSaving(true);
    try {
      await updateAccountBranding(brandingPayload(t.form));
      t.reset(t.form);
      // The navigation reads the same row: show the new logo and colours now.
      await queryClient.invalidateQueries({ queryKey: ['navigation'] });
      toast.success('Branding saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The branding could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const bg1 = t.form.backgroundColor1 ? `#${t.form.backgroundColor1}` : '#1f2937';
  const previewBg = (key: LogoKey) => (key === 'siteLogo' ? bg1 : key === 'darkLogo' ? '#ffffff' : '#1a2744');

  // Locked: nothing can change, so there is no Save or Revert.
  const actions = locked ? undefined : (
    <div className="flex flex-wrap items-center gap-3">
      {t.dirty && (
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-blue-700">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            Unsaved changes
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            aria-label="Revert all changes"
            onClick={t.revertAll}
          >
            Revert
          </Button>
        </div>
      )}
      <Button onClick={() => void save()} disabled={!t.dirty || loading || failed} loading={saving}>
        Save
      </Button>
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden p-6">
      <div className="flex-shrink-0">
        <PageHeader
          title="Branding"
          subtitle={locked ? 'Account logos and theme colors' : 'Manage account logos and theme colors'}
          actions={actions}
        />
      </div>
      {loading ? (
        <SpinnerOverlay label="Loading branding…" />
      ) : failed ? (
        <p className="py-16 text-center text-sm text-slate-500">The branding could not be loaded. Reload the page to try again.</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pb-6">
          <AdminLockedFieldset>
            <section className="mb-8 max-w-4xl">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Theme Colors</h3>
              <div className="grid gap-4 md:grid-cols-2">
                {COLOR_FIELDS.map(({ key, label: text, sublabel }) => (
                  <ColorField
                    key={key}
                    id={`branding-${key}`}
                    label={text}
                    sublabel={sublabel}
                    value={t.form[key]}
                    badge={badge(key, text)}
                    onChange={(v) => t.patch({ [key]: v } as Partial<BrandingForm>)}
                  />
                ))}
              </div>
            </section>

            <section className="max-w-4xl">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Logos</h3>
              <div className="grid gap-x-6 gap-y-6 md:grid-cols-2">
                {LOGO_SETS.map(({ key, squareKey, label: text, hint, svg }) => (
                  <div key={key} className="space-y-1.5" data-testid={`logo-set-${key}`}>
                    <div>
                      <h4 className="text-xs font-semibold text-slate-700">{text}</h4>
                      <p className="text-[11px] leading-tight text-slate-400">{hint}</p>
                    </div>
                    <div className="flex gap-2">
                      {([
                        [squareKey, 'Square', 'square'],
                        [key, 'Full', 'rectangle'],
                      ] as const).map(([field, slotLabel, aspect]) => (
                        <LogoSlot
                          key={field}
                          label={`${text} ${slotLabel}`}
                          slotLabel={slotLabel}
                          aspect={aspect}
                          value={t.form[field]}
                          url={resolveLogoUrl(t.form[field], accountId)}
                          fallbackUrl={legacyFallbackUrl(field, accountId)}
                          background={previewBg(key)}
                          badge={badge(field, `${text} ${slotLabel}`)}
                          onChange={() => {
                            if (!locked) setPickerFor({ key: field, svg });
                          }}
                          onPreview={(url) => setPreview({ url, bg: key === 'darkLogo' ? 'white' : 'dark' })}
                        />
                      ))}
                    </div>
                    {key === 'siteLogo' && (
                      <SiteLogoCaption
                        form={t.form}
                        logoUrl={resolveLogoUrl(t.form.siteLogo, accountId) || legacyFallbackUrl('siteLogo', accountId)}
                        background={bg1}
                        badge={badge}
                        patch={t.patch}
                      />
                    )}
                  </div>
                ))}
              </div>
            </section>
          </AdminLockedFieldset>
        </div>
      )}

      <EntityImagePicker
        entityType="logo"
        entityId={null}
        folder="logos"
        imageKind={pickerFor?.svg ? 'svg' : 'raster'}
        open={pickerFor !== null}
        onOpenChange={(open) => {
          if (!open) setPickerFor(null);
        }}
        currentImageUrl={pickerFor ? resolveLogoUrl(t.form[pickerFor.key], accountId) : null}
        title="Choose a logo"
        onSelected={(url) => {
          if (pickerFor) t.patch({ [pickerFor.key]: url } as Partial<BrandingForm>);
          setPickerFor(null);
        }}
      />

      {preview && <LogoPreviewModal url={preview.url} defaultBg={preview.bg} brandColor={bg1} onClose={() => setPreview(null)} />}
    </div>
  );
}

// ─── Colour field ──────────────────────────────────────────────────────────────

function ColorField({
  id,
  label,
  sublabel,
  value,
  badge,
  onChange,
}: {
  id: string;
  label: string;
  sublabel: string;
  value: string;
  badge: ReactNode;
  onChange: (hex: string) => void;
}) {
  const locked = useFieldLocked();
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className={LABEL_CLASS}>
          <span>{label}</span>
        </label>
        {badge}
      </div>
      <p className={cn(DESCRIPTION_CLASS, '-mt-0.5')}>{sublabel}</p>
      <div className="flex items-center gap-2">
        {locked ? (
          <ColorSwatch value={value} className="h-9 w-12" />
        ) : (
          <input
            type="color"
            aria-label={`${label} picker`}
            value={toHexInput(value)}
            onChange={(e) => onChange(e.target.value.replace(/^#/, ''))}
            className="h-9 w-12 cursor-pointer rounded border border-slate-200 p-0.5"
          />
        )}
        <Input
          id={id}
          value={value}
          placeholder="000000"
          maxLength={6}
          leftAddon={<span className="text-sm text-slate-400">#</span>}
          onChange={(e) => onChange(e.target.value.replace(/^#/, '').replace(/[^0-9a-fA-F]/g, '').slice(0, 6))}
          className="font-mono"
          wrapperClassName="flex-1"
        />
      </div>
    </div>
  );
}

/** A read-only colour: the swatch the picker would show, as plain content. */
function ColorSwatch({ value, className }: { value: string; className: string }) {
  return (
    <span aria-hidden="true" className={cn('block shrink-0 rounded border border-slate-200 p-0.5', className)}>
      <span className="block h-full w-full rounded-sm" style={value ? { backgroundColor: `#${value}` } : CHECKER} />
    </span>
  );
}

// ─── Logo slot ─────────────────────────────────────────────────────────────────

function LogoSlot({
  label,
  slotLabel,
  aspect,
  value,
  url,
  fallbackUrl,
  background,
  badge,
  onChange,
  onPreview,
}: {
  label: string;
  slotLabel: string;
  aspect: 'square' | 'rectangle';
  /** The stored value (URL or legacy file name). */
  value: string;
  url: string;
  fallbackUrl: string;
  background: string;
  badge: ReactNode;
  onChange: () => void;
  onPreview: (url: string) => void;
}) {
  const locked = useFieldLocked();
  const [src, setSrc] = useState(url || fallbackUrl);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setSrc(url || fallbackUrl);
    setBroken(false);
  }, [url, fallbackUrl]);

  const shown = broken ? '' : src;
  const aspectClass = aspect === 'rectangle' ? 'aspect-[16/9]' : 'aspect-square';

  return (
    <div className={cn('min-w-0', aspect === 'rectangle' ? 'flex-[16]' : 'flex-[9]')}>
      <div className="mb-1 flex items-center gap-1">
        <span className="text-[11px] font-medium text-slate-600">{slotLabel}</span>
        {badge}
      </div>
      <div className="rounded-lg border-2 border-dashed border-slate-300">
        {shown ? (
          <div className="group relative overflow-hidden rounded-lg" style={CHECKER}>
            <div className="absolute inset-0" style={{ backgroundColor: background, opacity: 0.9 }} />
            <img
              src={shown}
              alt={label}
              className={cn('relative z-[1] w-full object-contain', aspectClass)}
              onError={() => {
                if (fallbackUrl && src !== fallbackUrl) setSrc(fallbackUrl);
                else setBroken(true);
              }}
            />
            <button
              type="button"
              onClick={() => onPreview(shown)}
              aria-label={`Preview ${label}`}
              title="Preview"
              className="absolute left-1 top-1 z-[2] flex h-6 w-6 items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity hover:bg-black/70 focus:opacity-100 group-hover:opacity-100"
            >
              <ZoomIn className="h-3 w-3 text-white" aria-hidden />
            </button>
            {!locked && (
              <button
                type="button"
                onClick={onChange}
                aria-label={`Change ${label}`}
                title="Change image"
                className="absolute right-1 top-1 z-[2] flex h-6 w-6 items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity hover:bg-black/70 focus:opacity-100 group-hover:opacity-100"
              >
                <Upload className="h-3 w-3 text-white" aria-hidden />
              </button>
            )}
          </div>
        ) : locked ? (
          // An empty slot has no Upload (as EntityImageTile reads when locked).
          <div className={cn('flex w-full flex-col items-center justify-center rounded-lg', aspectClass)}>
            <ImageIcon className="mb-1 h-5 w-5 text-slate-300" aria-hidden />
            <span className="text-[10px] text-slate-400">No image</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={onChange}
            aria-label={`Upload ${label}`}
            className={cn('flex w-full flex-col items-center justify-center rounded-lg hover:bg-slate-50', aspectClass)}
          >
            <Upload className="mb-1 h-5 w-5 text-slate-400" aria-hidden />
            <span className="text-[10px] text-slate-500">Upload</span>
          </button>
        )}
      </div>
      {shown && value && !/^https?:\/\//i.test(value) && <p className="break-all text-[9px] leading-tight text-slate-400">{value}</p>}
      {shown && !value && <p className="text-[9px] italic text-slate-400">legacy fallback</p>}
    </div>
  );
}

// ─── Site logo caption ─────────────────────────────────────────────────────────

function SiteLogoCaption({
  form,
  logoUrl,
  background,
  badge,
  patch,
}: {
  form: BrandingForm;
  logoUrl: string;
  background: string;
  badge: (key: keyof BrandingForm) => ReactNode;
  patch: (next: Partial<BrandingForm>) => void;
}) {
  const locked = useFieldLocked();
  const small = 'text-[11px] font-medium text-slate-600';
  const fontOptions = useMemo(
    () => [{ value: '', label: 'Default (sidebar font)' }, ...LOGO_SUB_TEXT_FONT_OPTIONS.map((f) => ({ value: f.key, label: f.label }))],
    [],
  );

  return (
    <div className="space-y-1.5 pt-0.5">
      <SubTextPreview form={form} logoUrl={logoUrl} background={background} />

      <div className="flex items-center gap-1.5">
        <label htmlFor="branding-sub-text" className="text-xs font-semibold text-slate-700">
          Sub Text
        </label>
        {badge('siteLogoSubText')}
      </div>
      <Input
        id="branding-sub-text"
        value={form.siteLogoSubText}
        maxLength={SITE_LOGO_SUB_TEXT_MAX}
        placeholder="e.g. the practice name"
        onChange={(e) => patch({ siteLogoSubText: e.target.value })}
        className="h-8 text-sm"
      />
      <p className="text-[11px] leading-tight text-slate-400">Shown under the logo in Prospera navigation.</p>

      <div className="flex flex-wrap gap-2 pt-0.5">
        <div className="min-w-[8rem] flex-1 space-y-1">
          <div className="flex items-center gap-1.5">
            <label htmlFor="branding-sub-text-font" className={small}>
              Font
            </label>
            {badge('siteLogoSubTextFont')}
          </div>
          <Select<string>
            id="branding-sub-text-font"
            options={fontOptions}
            value={form.siteLogoSubTextFont}
            onChange={(v) => patch({ siteLogoSubTextFont: v ?? '' })}
            renderOption={(o) => <span style={{ fontFamily: logoSubTextFontStack(o.value) }}>{o.label}</span>}
            className="h-8 text-sm"
          />
        </div>
        <NumberKnob
          id="branding-sub-text-size"
          label="Size"
          badge={badge('siteLogoSubTextSize')}
          value={form.siteLogoSubTextSize}
          placeholder={String(LOGO_SUB_TEXT_DEFAULT_SIZE_PX)}
          min={6}
          max={64}
          onChange={(v) => patch({ siteLogoSubTextSize: v.replace(/[^0-9]/g, '').slice(0, 3) })}
        />
        <NumberKnob
          id="branding-sub-text-spacing"
          label="Spacing"
          badge={badge('siteLogoSubTextLetterSpacing')}
          value={form.siteLogoSubTextLetterSpacing}
          placeholder={String(LOGO_SUB_TEXT_DEFAULT_LETTER_SPACING_PX)}
          min={-20}
          max={20}
          step={0.1}
          onChange={(v) => patch({ siteLogoSubTextLetterSpacing: v.replace(/[^0-9.-]/g, '').slice(0, 6) })}
        />
        <div className="shrink-0 space-y-1">
          <div className="flex items-center gap-1.5">
            <span className={small}>Color</span>
            {badge('siteLogoSubTextColor')}
          </div>
          <div className="flex h-8 items-center gap-1">
            {locked ? (
              <ColorSwatch value={form.siteLogoSubTextColor || 'ffffff'} className="h-8 w-10" />
            ) : (
              <input
                type="color"
                aria-label="Sub text color"
                value={toHexInput(form.siteLogoSubTextColor || 'ffffff')}
                onChange={(e) => patch({ siteLogoSubTextColor: e.target.value.replace(/^#/, '') })}
                className="h-8 w-10 cursor-pointer rounded border border-slate-200 p-0.5"
              />
            )}
            {form.siteLogoSubTextColor && !locked ? (
              <button
                type="button"
                onClick={() => patch({ siteLogoSubTextColor: '' })}
                className="text-slate-400 hover:text-slate-700"
                title="Use the sidebar default"
                aria-label="Use the sidebar default color"
              >
                <RotateCcw className="h-3 w-3" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <span className={small}>Justification</span>
            {badge('siteLogoSubTextAlign')}
          </div>
          <div
            role="radiogroup"
            aria-label="Justification"
            aria-readonly={locked || undefined}
            className="flex h-8 w-fit overflow-hidden rounded-md border border-slate-200"
          >
            {LOGO_SUB_TEXT_ALIGNMENTS.map((align) => (
              <button
                key={align}
                type="button"
                role="radio"
                aria-checked={form.siteLogoSubTextAlign === align}
                onClick={() => {
                  if (!locked) patch({ siteLogoSubTextAlign: align });
                }}
                className={cn(
                  'border-r border-slate-200 px-3 text-xs capitalize transition-colors last:border-r-0',
                  form.siteLogoSubTextAlign === align
                    ? 'bg-blue-50 font-medium text-blue-700'
                    : cn('bg-white text-slate-600', !locked && 'hover:bg-slate-50'),
                  locked && 'cursor-default',
                )}
              >
                {align}
              </button>
            ))}
          </div>
        </div>
        <NumberKnob
          id="branding-sub-text-offset"
          label="Move Y"
          badge={badge('siteLogoSubTextOffsetY')}
          value={form.siteLogoSubTextOffsetY}
          placeholder="0"
          min={-60}
          max={60}
          onChange={(v) => patch({ siteLogoSubTextOffsetY: v.replace(/[^0-9-]/g, '').slice(0, 4) })}
          width="w-20"
        />
      </div>
    </div>
  );
}

function NumberKnob({
  id,
  label,
  badge,
  value,
  placeholder,
  min,
  max,
  step,
  onChange,
  width = 'w-16',
}: {
  id: string;
  label: string;
  badge: ReactNode;
  value: string;
  placeholder: string;
  min: number;
  max: number;
  step?: number;
  onChange: (v: string) => void;
  width?: string;
}) {
  return (
    <div className={cn(width, 'shrink-0 space-y-1')}>
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-[11px] font-medium text-slate-600">
          {label}
        </label>
        {badge}
      </div>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 px-2 text-sm"
      />
    </div>
  );
}

/**
 * The site logo and caption drawn as the navigation draws them (Sidebar.tsx):
 * the logo at the nav's height and its own aspect, the caption exactly as wide
 * as the logo, inside a sidebar-width header on the sidebar colour.
 */
function SubTextPreview({ form, logoUrl, background }: { form: BrandingForm; logoUrl: string; background: string }) {
  const text = form.siteLogoSubText.trim();
  return (
    <div className="flex justify-center rounded border border-slate-200 py-3" style={{ backgroundColor: background }} data-testid="site-logo-preview">
      <div className="flex w-[256px] max-w-full flex-col px-4">
        <div className="max-w-full self-center">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="block w-auto max-w-full object-contain" style={{ height: `${LOGO_SUB_TEXT_NAV_LOGO_HEIGHT_PX}px` }} />
          ) : null}
          {text ? (
            <p
              className="mt-0.5 w-0 min-w-full break-words leading-tight text-white/70"
              style={{
                fontSize: `${logoSubTextFontSize(Number(form.siteLogoSubTextSize) || 0)}px`,
                letterSpacing: logoSubTextLetterSpacing(Number(form.siteLogoSubTextLetterSpacing) || 0),
                textAlign: (form.siteLogoSubTextAlign || 'center') as CSSProperties['textAlign'],
                fontFamily: logoSubTextFontStack(form.siteLogoSubTextFont),
                color: logoSubTextColor(form.siteLogoSubTextColor),
                position: 'relative',
                top: `${logoSubTextOffsetY(Number(form.siteLogoSubTextOffsetY) || 0)}px`,
              }}
            >
              {text}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Logo preview ──────────────────────────────────────────────────────────────

type PreviewBg = 'white' | 'dark' | 'brand' | 'checker';

function LogoPreviewModal({
  url,
  defaultBg,
  brandColor,
  onClose,
}: {
  url: string;
  defaultBg: 'white' | 'dark';
  brandColor: string;
  onClose: () => void;
}) {
  const [bg, setBg] = useState<PreviewBg>(defaultBg);
  const options: { key: PreviewBg; label: string; color: string | null }[] = [
    { key: 'white', label: 'White', color: '#ffffff' },
    { key: 'dark', label: 'Dark', color: '#1a2744' },
    { key: 'brand', label: 'Brand', color: brandColor },
    { key: 'checker', label: 'Transparent', color: null },
  ];
  const active = options.find((o) => o.key === bg);

  return (
    <Modal isOpen onClose={onClose} size="lg" title={<h2 className="text-lg font-semibold leading-none text-slate-900">Logo Preview</h2>}>
      <div className="space-y-4">
        <div
          className="flex min-h-[300px] items-center justify-center overflow-hidden rounded-lg border border-slate-200 transition-colors"
          style={bg === 'checker' ? { ...CHECKER, backgroundSize: '20px 20px' } : { backgroundColor: active?.color ?? '#1a2744' }}
        >
          <img src={url} alt="Logo preview" className="max-h-[400px] max-w-full object-contain p-8" />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              aria-pressed={bg === o.key}
              onClick={() => setBg(o.key)}
              className={cn(
                'flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                bg === o.key ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              <span
                className="h-4 w-4 shrink-0 overflow-hidden rounded-full border border-slate-300"
                style={o.color ? { backgroundColor: o.color } : { ...CHECKER, backgroundSize: '6px 6px' }}
              />
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
